import store from "../store";

const ATTRIBUTION_WINDOW_DAYS = 30;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DIRECT_SOURCE = 'direct';

// Order matters: Instagram and Messenger UAs can also carry Facebook tokens.
const IN_APP_BROWSERS = [
  { source: 'instagram', pattern: /Instagram/i },
  { source: 'threads', pattern: /Barcelona/i },
  { source: 'messenger', pattern: /MessengerForiOS|Orca-Android|MessengerLite/i },
  { source: 'facebook', pattern: /FBAN|FBAV|FB_IAB/i },
  { source: 'tiktok', pattern: /musical_ly|trill_|BytedanceWebview|TikTok/i },
  { source: 'line', pattern: /\bLine\//i },
];

// fbclid is shared by Facebook and Instagram, so it can't tell them apart.
const CLICK_IDS = [
  { param: 'fbclid', source: 'meta', medium: 'social' },
  { param: 'ttclid', source: 'tiktok', medium: 'paid' },
  { param: 'gclid', source: 'google', medium: 'paid' },
  { param: 'twclid', source: 'twitter', medium: 'paid' },
];

export const CLICK_ID_PARAMS = CLICK_IDS.map(item => item.param);

// Matches web hosts and Android package names (android-app:// referrers from Custom Tabs).
const REFERRER_SOURCES = [
  { source: 'instagram', pattern: /(^|\.)instagram\.com$|^com\.instagram\.android$/ },
  { source: 'facebook', pattern: /(^|\.)facebook\.com$|(^|\.)fb\.com$|^com\.facebook\.(katana|lite|orca)$/ },
  { source: 'tiktok', pattern: /(^|\.)tiktok\.com$|^com\.zhiliaoapp\.musically$|^com\.ss\.android\.ugc\.trill$/ },
  { source: 'twitter', pattern: /^t\.co$|(^|\.)x\.com$|(^|\.)twitter\.com$|^com\.twitter\.android$/ },
  { source: 'threads', pattern: /(^|\.)threads\.net$|(^|\.)threads\.com$|^com\.instagram\.barcelona$/ },
  { source: 'whatsapp', pattern: /(^|\.)whatsapp\.com$|^com\.whatsapp(\.w4b)?$/ },
  { source: 'telegram', pattern: /^t\.me$|^org\.telegram\.messenger$/ },
  { source: 'line', pattern: /(^|\.)line\.me$|^jp\.naver\.line\.android$/ },
  { source: 'google', pattern: /(^|\.)google\.[a-z.]+$|^com\.google\.android\.googlequicksearchbox$/ },
];

// URL() returns an empty hostname for non-special schemes on older Chrome.
const ANDROID_APP_REFERRER = /^android-app:\/\/([^/]+)/;

const getReferrerHost = () => {
  if (!document.referrer) return '';

  const androidAppMatch = document.referrer.match(ANDROID_APP_REFERRER);
  if (androidAppMatch) return androidAppMatch[1];

  try {
    return new URL(document.referrer).hostname;
  } catch (error) {
    return '';
  }
};

const buildTouch = ({ source, medium, campaign = '', method }) => ({
  source,
  medium,
  campaign,
  method,
  landing_page: window.location.pathname,
  referrer: getReferrerHost(),
  captured_at: new Date().toISOString(),
});

const detectFromUtm = (params) => {
  const utmSource = params.get('utm_source');
  if (!utmSource) return null;

  return {
    source: utmSource.toLowerCase(),
    medium: params.get('utm_medium') || '',
    campaign: params.get('utm_campaign') || '',
    method: 'utm',
  };
};

const detectFromUserAgent = () => {
  const inAppBrowser = IN_APP_BROWSERS.find(item => item.pattern.test(navigator.userAgent));
  if (!inAppBrowser) return null;

  return { source: inAppBrowser.source, medium: 'social', method: 'user_agent' };
};

const detectFromClickId = (params) => {
  const clickId = CLICK_IDS.find(item => params.has(item.param));
  if (!clickId) return null;

  return { source: clickId.source, medium: clickId.medium, method: 'click_id' };
};

const detectFromReferrer = () => {
  const referrerHost = getReferrerHost();
  if (!referrerHost || referrerHost === window.location.hostname) return null;

  const knownSource = REFERRER_SOURCES.find(item => item.pattern.test(referrerHost));
  if (knownSource) return { source: knownSource.source, medium: 'social', method: 'referrer' };

  return { source: referrerHost, medium: 'referral', method: 'referrer' };
};

/**
 * Detects where the current landing came from: UTM, in-app browser, click id, then referrer.
 * @returns {Object} touch with source, medium, campaign, method, landing_page, referrer, captured_at
 */
export const detectTrafficSource = () => {
  const params = new URLSearchParams(window.location.search);
  const detected = detectFromUtm(params)
    || detectFromUserAgent()
    || detectFromClickId(params)
    || detectFromReferrer()
    || { source: DIRECT_SOURCE, medium: 'none', method: 'none' };

  return buildTouch(detected);
};

const isTouchExpired = (touch) => {
  if (!touch || !touch.captured_at) return true;

  const ageInMs = Date.now() - new Date(touch.captured_at).getTime();
  return ageInMs > ATTRIBUTION_WINDOW_DAYS * DAY_IN_MS;
};

/**
 * Detects the landing source and stores it as first/last touch in the persisted store.
 * @returns {Object} the touch detected for this landing
 */
export const captureTrafficSource = () => {
  const currentTouch = detectTrafficSource();
  const { first_touch: firstTouch, last_touch: lastTouch } = store.getters.getTrafficSource;
  const isDirect = currentTouch.source === DIRECT_SOURCE;

  store.commit('setTrafficSource', {
    first_touch: isTouchExpired(firstTouch) ? currentTouch : firstTouch,
    // A direct revisit shouldn't erase the social source that brought the guest in.
    last_touch: isDirect && !isTouchExpired(lastTouch) ? lastTouch : currentTouch,
  });

  return currentTouch;
};



// WEBPACK FOOTER //
// ./src/utils/trafficSource.js